import { useEffect, useState, useMemo, useContext, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Axios from "../../../../utils/axios";
import { useEventLoader } from "../../../../hooks/Events/useEventLoader";
import AuthContext from "../../../../store/AuthContext";
import { getRegistrationByPersonAndEvent } from "../../../../Services/Registration";
import { getPaymentByRegistrationId } from "../../../../Services/Payment";
import { toastError } from "../../../../utils/toast";
import { getPayaMessageByStatusId } from "../../../../utils/payaErrorMessages";
import Input from "../../../../components/Atomos/Inputs/Input";
import RadioButton from "../../../../components/Atomos/Radio/RadioButton";
import {
  invoiceAddressTypeEnum,
  InvoiceAddressType,
  paymentMethodEnum,
} from "../../../../Enums/RegistrationEnum";
import { validateAndSanitizeEmail } from "../../../../utils/emailValidation";
import { generateInvoicePDFWithLogo, saveInvoicePDFToBlob } from "../../../../utils/generateInvoicePDF";
import { sendInvoice as sendInvoiceEmail, saveInvoiceHistory } from "../../../../Services/Invoice";
import { timestampToDateString } from "../../../../utils/dates";
import { getFirmById } from "../../../../Services/Firm";
import { getAccountingMailingAddressForInvoice } from "../../../../utils/firmInvoiceAddress";
import {
  calculateInvoiceAmount,
} from "../../../../utils/invoiceCalculations";

const EventPaymentPageWithLayout = () => {
  const { id: eventId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const event = useEventLoader(eventId);
  const { loggedPerson } = useContext(AuthContext);
  const [payaUrl, setPayaUrl] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [iframeKey, setIframeKey] = useState<number>(0);
  const [registrationId, setRegistrationId] = useState<string | null>(null);
  const [cc, setCc] = useState({ name: "", email: "" });
  const [selectedInvoiceAddress, setSelectedInvoiceAddress] =
    useState<InvoiceAddressType>("billing");
  const axiosInstance = useMemo(() => new Axios(), []);

  const reloadPaymentForm = useMemo(() => {
    return async () => {
      if (!eventId || !event || !loggedPerson?.id) {
        return;
      }

      try {
        const registrationData = await getRegistrationByPersonAndEvent(
          loggedPerson.id,
          eventId
        );

        if (!registrationData || !registrationData.id) {
          return;
        }

        const currency = event.fee?.currency || "USD";

        const origin = window.location.origin;
        let stylesheetUrl: string | undefined;

        if (origin.includes("localhost") || origin.includes("127.0.0.1")) {
          const ngrokBackendUrl = localStorage.getItem("ngrokBackendUrl");
          if (ngrokBackendUrl) {
            const cleanBackendUrl = ngrokBackendUrl.trim().replace(/\/+$/, "");
            stylesheetUrl = `${cleanBackendUrl}/api/paya/paya-hpp-styles.css`;
          }
        } else {
          stylesheetUrl = `${origin}/paya-hpp-styles.css`;
        }

        let callbackBaseUrl = origin;
        if (origin.includes("localhost") || origin.includes("127.0.0.1")) {
          const ngrokUrl = localStorage.getItem("ngrokUrl");
          if (ngrokUrl) {
            callbackBaseUrl = ngrokUrl.trim().replace(/\/+$/, "");
          }
        }
        const callbackUrl = `${callbackBaseUrl}/api/paya/callback`;

        const registrantName =
          [loggedPerson?.name, loggedPerson?.lastName]
            .filter(Boolean)
            .join(" ") ||
          registrationData.badgeName ||
          "Registrant";

        const eventName = event.title || "Event Registration";
        const descriptionValue = `${eventName} - ${registrationData.shortId}`;
        
        const requestBody: any = {
          method: "hpp-encrypted",
          currency: currency,
          // stylesheetUrl: stylesheetUrl,
          registrationId: registrationData.id,
          eventId: eventId,
          callbackUrl: callbackUrl,
          description: descriptionValue,
          customFields: [
            {
              id: "custom_data-eventName",
              value: eventName,
            },
            {
              id: "custom_data-registrantName",
              value: registrantName,
            }
          ],
        };

        const response = await axiosInstance.Post(
          "/paya/generate-payform-url",
          requestBody
        );

        const url = response.data?.hppUrl || response.data?.payFormUrl;
        if (response.data?.success && url) {
          setPayaUrl(url);
          setIframeKey((prev) => prev + 1);
        }
      } catch (err) {}
    };
  }, [event, eventId, loggedPerson, axiosInstance]);

  useEffect(() => {
    const contentDiv = document.getElementById("content-container");
    if (!contentDiv) {
      return;
    }

    const savedScrollPosition = sessionStorage.getItem("eventScrollPosition");
    const scrollPosition = savedScrollPosition
      ? parseInt(savedScrollPosition, 10)
      : null;

    if (scrollPosition === null || scrollPosition === 0) {
      return;
    }

    let isRestoringScroll = false;
    let scrollListener: ((e: Event) => void) | null = null;

    const restoreScroll = () => {
      if (contentDiv && scrollPosition > 0 && !isRestoringScroll) {
        isRestoringScroll = true;

        scrollListener = (e: Event) => {
          e.stopImmediatePropagation();
        };
        contentDiv.addEventListener("scroll", scrollListener, {
          capture: true,
        });

        contentDiv.scrollTo({
          top: scrollPosition,
          behavior: "auto",
        });

        setTimeout(() => {
          if (scrollListener) {
            contentDiv.removeEventListener("scroll", scrollListener, {
              capture: true,
            });
            scrollListener = null;
          }
          isRestoringScroll = false;
        }, 150);
      }
    };

    const timeout = setTimeout(() => {
      restoreScroll();
    }, 100);

    const setupIframeScrollRestore = () => {
      const iframe = document.querySelector(
        'iframe[title="Paya Payment Form"]'
      ) as HTMLIFrameElement;
      if (iframe && !isRestoringScroll) {
        if (iframe.contentDocument?.readyState === "complete") {
          restoreScroll();
        } else {
          const handleLoad = () => {
            setTimeout(restoreScroll, 200);
          };
          iframe.addEventListener("load", handleLoad, { once: true });
        }
      }
    };

    const iframeTimeout = setTimeout(() => {
      setupIframeScrollRestore();
    }, 300);

    return () => {
      clearTimeout(timeout);
      clearTimeout(iframeTimeout);
      if (scrollListener && contentDiv) {
        contentDiv.removeEventListener("scroll", scrollListener, {
          capture: true,
        });
      }
    };
  }, [payaUrl]);

  const handleSendPaidInvoice = useCallback(async (
    registrationId: string,
    eventId: string,
    transactionId: string,
    transactionAmount: number,
    ccEmail?: string | null,
    ccName?: string
  ) => {
    if (!event || !loggedPerson || !registrationId) {
      return;
    }

    try {
      const registration = await getRegistrationByPersonAndEvent(
        loggedPerson.id || "",
        eventId
      );

      if (!registration) {
        return;
      }

      const payment = await getPaymentByRegistrationId(registrationId);
      if (!payment) {
        return;
      }

      const feeForInvoice = event.fee;
      const additionalFeeValueForInvoice = parseFloat((registration as any).additionalFee || "0");
      const totalInvoiceAmount = calculateInvoiceAmount(
        event,
        registration,
        additionalFeeValueForInvoice,
      );
      const currency = feeForInvoice?.currency || "USD";

      let invoiceAddressData: {
        address1?: string;
        address2?: string;
        city?: string;
        state?: string;
        zipCode?: string;
        country?: string;
      } | undefined = undefined;

      let firmName: string | undefined = undefined;
      const firmId = registration.firmId || loggedPerson.firm;
      if (firmId) {
        try {
          const firmData = await getFirmById(firmId);
          if (firmData) {
            firmName = firmData.firmName || firmData.name || undefined;
            invoiceAddressData = getAccountingMailingAddressForInvoice(firmData);
          }
        } catch (firmError) {
        }
      }

      const eventName = (event.title || "Event")
        .replace(/[^a-zA-Z0-9\s-]/g, "")
        .replace(/\s+/g, "_");
      const memberName = ([loggedPerson.name, loggedPerson.lastName]
        .filter(Boolean)
        .join(" ") || registration.badgeName || "Member")
        .replace(/[^a-zA-Z0-9\s-]/g, "")
        .replace(/\s+/g, "_");
      const currentTimestamp = Math.floor(Date.now() / 1000);
      const date = new Date(currentTimestamp * 1000);
      const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
      const day = date.getUTCDate().toString().padStart(2, "0");
      const year = date.getUTCFullYear();
      const dateStr = `${month}-${day}-${year}`;
      const filename = `Paid_Invoice_${eventName}_${memberName}_${dateStr}.pdf`;

      const totalPaidAmount = transactionAmount || payment.amount || 0;
      const creditPaymentValue = 0;
      const amountRefundedValue = 0;
      const paidStatus: "paid" | "partial" | "unpaid" = "paid";

      const paymentMethodOptions = Object.entries(paymentMethodEnum).map(
        ([key, value]) => ({
          id: key,
          name: value,
        })
      );

      let pdfBlob: Blob | null = null;
      let savedPdfPath: string | null = null;

      try {
        let feeToUse = event.fee;
        if ((registration as any).prepaid === true && event.fee) {
          feeToUse = {
            ...event.fee,
            fee: 0,
            memberFee: 0,
            lateFee: 0,
            specialEventFee: event.fee.specialEventFee ? 0 : undefined,
          };
        }

        const programForInvoice = event.program?.map((p: any) => ({
          id: p.id,
          title: p.title,
          registrationPageSessionTitle: p.registrationPageSessionTitle,
          fee: p.fee,
          specialEventGuestFee: p.specialEventGuestFee,
        }));

        pdfBlob = await generateInvoicePDFWithLogo({
          event: {
            id: event.id,
            title: event.title,
            startDate: event.startDate,
            location: event.location,
            fee: feeToUse,
            program: programForInvoice,
          },
          person: {
            name: [loggedPerson.name, loggedPerson.lastName]
              .filter(Boolean)
              .join(" ") || registration.badgeName || "",
            email: loggedPerson.firmEmail || "",
          },
          firm: firmName ? { name: firmName } : null,
          badgeName: registration.badgeName,
          guests: registration.guests,
          guestName: registration.guestName,
          guestName2: registration.guestName2,
          eventPayment: totalPaidAmount.toString(),
          creditPayment: creditPaymentValue.toString(),
          amountRefunded: amountRefundedValue.toString(),
          additionalFee: additionalFeeValueForInvoice.toString(),
          paymentMethod: payment.paymentMethod || "creditCard",
          paymentMethodOptions,
          paid: paidStatus,
          notes: (registration as any).note || "",
          eventId,
          registrationId: registration.id,
          personId: loggedPerson.id,
          filename,
          invoiceAddress: invoiceAddressData,
          selectedSpecialEventIds: registration.selectedSpecialEventIds ?? [],
          specialEventGuestNames: registration.specialEventGuestNames,
          registrationDate: registration.registrationDate,
          isPrepaid: (registration as any).prepaid === true,
        });

        if (pdfBlob) {
          const storagePathOrUrl = await saveInvoicePDFToBlob(
            pdfBlob,
            filename,
            loggedPerson.id || "",
            eventId
          );

          if (storagePathOrUrl) {
            if (storagePathOrUrl.startsWith("http")) {
              savedPdfPath = storagePathOrUrl;
            } else {
              const storageAccount = process.env.REACT_APP_STORAGE_ACCOUNT_NAME || process.env.REACT_APP_STORAGE_ACCOUNT || "wsgfilestorageqa";
              const container = process.env.REACT_APP_STORAGE_ACCOUNT_CONTAINER_NAME || process.env.REACT_APP_STORAGE_CONTAINER || "testing";
              const cleanStoragePath = storagePathOrUrl.startsWith("/") ? storagePathOrUrl.substring(1) : storagePathOrUrl;
              savedPdfPath = `https://${storageAccount}.blob.core.windows.net/${container}/${cleanStoragePath}`;
            }
          }
        }
      } catch (pdfError) {
      }

      try {
        const attendeeEmailAddress = loggedPerson.firmEmail || "";
        
        if (!attendeeEmailAddress) {
          return;
        }

        await sendInvoiceEmail({
          registrationId: registration.id || "",
          eventId: eventId,
          invoiceType: "Paid Invoice",
          ccName: ccName?.trim() || undefined,
          ccEmail: ccEmail?.trim() || undefined,
          attendeeEmail: attendeeEmailAddress,
          currency: currency,
          pdfPath: savedPdfPath || undefined,
          amount: totalInvoiceAmount,
          paymentStatus: "completed",
          filename: filename,
        });

        try {
          const oneHourInMs = 60 * 60 * 1000;
          const registrationTimestamp = registration.registrationDate 
            ? registration.registrationDate * 1000 
            : Date.now();
          const isOlderThanOneHour = (Date.now() - registrationTimestamp) > oneHourInMs;
          const sentByValue = isOlderThanOneHour ? "System" : "orig-reg";

          await saveInvoiceHistory({
            registrationId: registration.id || "",
            eventId: eventId,
            currency: currency,
            amount: totalInvoiceAmount,
            paymentStatus: "completed",
            pdfPath: savedPdfPath || undefined,
            filename: filename,
            ccEmail: ccEmail?.trim() || undefined,
            ccName: ccName?.trim() || undefined,
            sentBy: sentByValue,
          });
        } catch (historyError) {
        }
      } catch (emailError) {
      }
    } catch (error) {
      }
  }, [event, loggedPerson]);

  useEffect(() => {
    const loadPaymentUrl = async () => {
      if (!eventId || !event || !loggedPerson?.id) {
        return;
      }

      try {
        setError(null);

        const registrationData = await getRegistrationByPersonAndEvent(
          loggedPerson.id,
          eventId
        );

        if (!registrationData || !registrationData.id) {
          setError(
            "Registration not found. Please complete registration first."
          );
          return;
        }

        setRegistrationId(registrationData.id);

        const savedCcEmail = sessionStorage.getItem(`ccEmail_${eventId}`);
        if (savedCcEmail) {
          setCc((prev) => ({ ...prev, email: savedCcEmail }));
        }

        const currency = event.fee?.currency || "USD";

        const origin = window.location.origin;
        let stylesheetUrl: string | undefined;

        if (origin.includes("localhost") || origin.includes("127.0.0.1")) {
          const ngrokBackendUrl = localStorage.getItem("ngrokBackendUrl");
          if (ngrokBackendUrl) {
            const cleanBackendUrl = ngrokBackendUrl.trim().replace(/\/+$/, "");
            stylesheetUrl = `${cleanBackendUrl}/api/paya/paya-hpp-styles.css`;
          }
        } else {
          stylesheetUrl = `${origin}/paya-hpp-styles.css`;
        }

        let callbackBaseUrl = origin;
        if (origin.includes("localhost") || origin.includes("127.0.0.1")) {
          const ngrokBackendUrl = localStorage.getItem("ngrokBackendUrl");
          if (ngrokBackendUrl) {
            callbackBaseUrl = ngrokBackendUrl.trim().replace(/\/+$/, "");
          }
        }
        const callbackUrl = `${callbackBaseUrl}/api/paya/callback`;

        const registrantName =
          [loggedPerson?.name, loggedPerson?.lastName]
            .filter(Boolean)
            .join(" ") ||
          registrationData.badgeName ||
          "Registrant";

        const eventName = event.title || "Event Registration";
        const descriptionValue = `${eventName} - ${registrationData.shortId}`;

        const requestBody: any = {
          method: "hpp-encrypted",
          currency: currency,
          registrationId: registrationData.id,
          eventId: eventId,
          callbackUrl: callbackUrl,
          description: descriptionValue,
          customFields: [
            {
              id: "custom_data-eventName",
              value: eventName,
            },
            {
              id: "custom_data-registrantName",
              value: registrantName,
            }
          ],
        };

        const response = await axiosInstance.Post(
          "/paya/generate-payform-url",
          requestBody
        );
        const url = response.data?.hppUrl || response.data?.payFormUrl;

        if (response.data?.success && url) {
          setPayaUrl(url);
        } else {
          const errorMessage =
            response.data?.error ||
            response.data?.message ||
            "Failed to generate payment URL";
          setError(errorMessage);
        }
      } catch (err: any) {
        setError(
          err?.response?.data?.error ||
            err?.response?.data?.message ||
            "Error loading payment page. Please try again later."
        );
      }
    };

    if (event && loggedPerson) {
      loadPaymentUrl();
    }
  }, [event, eventId, loggedPerson, axiosInstance]);

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      const isPayaOrigin =
        event.origin.includes("payaconnect.com") ||
        event.origin.includes("sandbox.payaconnect.com");

      if (!isPayaOrigin) {
        return;
      }

      let data = event.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          return;
        }
      }

      const statusId = data?.status_id;
      const transactionId = data?.transaction_id;

      const isSuccess = statusId === 101;
      const isFailure =
        statusId !== undefined && statusId !== null && statusId !== 101;

      if (isFailure) {
        const errorMsg =
          getPayaMessageByStatusId(statusId) ||
          data.verbiage ||
          "Payment failed. Please try again.";

        toastError(errorMsg, { autoClose: false });
        reloadPaymentForm();
        return;
      }

      if (isSuccess) {
        if (registrationId && eventId) {
          try {
            const payload = {
              transaction_id: transactionId,
              status_id: statusId,
              transaction_amount: data.transaction_amount,
              currency: data.currency || "USD",
              registrationId: registrationId,
              eventId: eventId,
            };

            const paymentResponse = await axiosInstance.Post("/paya/process-payment", payload);
            
            if (!paymentResponse.data?.success) {
              const errorMsg = paymentResponse.data?.error || paymentResponse.data?.message || "Error processing payment";
              toastError(errorMsg, { autoClose: false });
            }

            // Enviar invoice con PDF después de procesar el pago exitosamente
            try {
              const ccEmail = eventId
                ? sessionStorage.getItem(`ccEmail_${eventId}`)
                : null;
              const ccName = cc.name?.trim() || undefined;

              await handleSendPaidInvoice(
                registrationId,
                eventId,
                transactionId,
                data.transaction_amount,
                ccEmail,
                ccName
              );

              if (eventId) {
                sessionStorage.removeItem(`ccEmail_${eventId}`);
              }
            } catch (invoiceError: any) {
              console.error("Error sending paid invoice:", invoiceError);
            }
          } catch (error: any) {
            const errorMsg = error?.response?.data?.error || error?.response?.data?.message || error?.message || "Error processing payment";
            toastError(`Payment processed but error saving: ${errorMsg}`, { autoClose: false });
          }
        }

        if (eventId) {
          const registrationUrl = `/events/${eventId}/registration`;
          sessionStorage.removeItem("eventScrollPosition");
          sessionStorage.setItem("payaPaymentSuccess", "1");
          if (window.top && window.top !== window.self) {
            window.top.location.href = registrationUrl;
          } else {
            navigate(registrationUrl);
          }
        }
        return;
      }

      toastError("Payment status unknown. Please try again.", {
        autoClose: false,
      });
      reloadPaymentForm();
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [
    eventId,
    navigate,
    reloadPaymentForm,
    registrationId,
    axiosInstance,
    loggedPerson?.firmEmail,
    cc.name,
    handleSendPaidInvoice,
  ]);

  const handleIframeLoad = useCallback(async () => {
    try {
      const iframe = document.querySelector(
        'iframe[title="Paya Payment Form"]'
      ) as HTMLIFrameElement;
      
      const setDescriptionField = async (currentRegistrationId: string | null) => {
        let regId = currentRegistrationId;
        if (!regId && eventId && loggedPerson?.id) {
          try {
            const registrationData = await getRegistrationByPersonAndEvent(
              loggedPerson.id,
              eventId
            );
            if (registrationData?.id) {
              regId = registrationData.id;
            }
          } catch (err) {
          }
        }
        
        try {
        } catch (iframeError) {
        }
      };
      
      await setDescriptionField(registrationId);
      
      setTimeout(async () => {
        await setDescriptionField(registrationId);
      }, 500);
      
      setTimeout(async () => {
        await setDescriptionField(registrationId);
      }, 1500);
      
      if (iframe?.contentWindow?.location) {
        const iframeUrl = iframe.contentWindow.location.href;
        if (iframeUrl.includes("/registration/success")) {
          const registrationUrl = `/events/${eventId}/registration`;
          sessionStorage.removeItem("eventScrollPosition");
          sessionStorage.setItem("payaPaymentSuccess", "1");
          if (window.top && window.top !== window.self) {
            window.top.location.href = registrationUrl;
          } else {
            navigate(registrationUrl);
          }
        }
      }
    } catch (e) { 
    }
  }, [registrationId, eventId, loggedPerson, event, navigate]);

  if (!event) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-8">
        <div className="text-lg">Loading event information...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-2xl w-full">
          <h2 className="text-xl font-semibold text-red-800 mb-2">
            Payment Error
          </h2>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={() => {
              setError(null);
              const loadUrl = async () => {
                if (eventId && event && loggedPerson?.id) {
                  try {
                      const registrationData =
                      await getRegistrationByPersonAndEvent(
                        loggedPerson.id,
                        eventId
                      );
                    if (registrationData?.id) {
                      const currency = event.fee?.currency || "USD";
                      const origin = window.location.origin;
                      let stylesheetUrl: string | undefined;

                      if (
                        origin.includes("localhost") ||
                        origin.includes("127.0.0.1")
                      ) {
                        const ngrokBackendUrl =
                          localStorage.getItem("ngrokBackendUrl");
                        if (ngrokBackendUrl) {
                          const cleanBackendUrl = ngrokBackendUrl
                            .trim()
                            .replace(/\/+$/, "");
                          stylesheetUrl = `${cleanBackendUrl}/api/paya/paya-hpp-styles.css`;
                        }
                      } else {
                        stylesheetUrl = `${origin}/paya-hpp-styles.css`;
                      }

                      let callbackBaseUrlRetry = origin;
                      if (
                        origin.includes("localhost") ||
                        origin.includes("127.0.0.1")
                      ) {
                        const ngrokBackendUrl =
                          localStorage.getItem("ngrokBackendUrl");
                        if (ngrokBackendUrl) {
                          callbackBaseUrlRetry = ngrokBackendUrl
                            .trim()
                            .replace(/\/+$/, "");
                        }
                      }
                      const callbackUrlRetry = `${callbackBaseUrlRetry}/api/paya/callback`;

                      const requestBody: any = {
                        method: "hpp-encrypted",
                        currency: currency,
                        registrationId: registrationData.id,
                        eventId: eventId,
                        callbackUrl: callbackUrlRetry,
                      };

                      const response = await axiosInstance.Post(
                        "/paya/generate-payform-url",
                        requestBody
                      );

                      if (response.data?.success && response.data?.hppUrl) {
                        setPayaUrl(response.data.hppUrl);
                      }
                    }
                  } catch (err) {
                    setError(
                      "Failed to reload payment form. Please refresh the page."
                    );
                  }
                }
              };
              loadUrl();
            }}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col w-full relative"
    >
       <p className="text-primary font-decimal text-2xl font-normal">
        Secure Credit Card Payment
      </p>
      <p className="text-gray-font text-sm font-semibold mt-2">
        Online Payment Form
      </p>
      <p className="text-gray-font text-sm font-normal">
        {`You have elected to pay the registration fee for the ${event.title} by `}
        <strong className="font-semibold">Credit Card Online.</strong>
      </p>{" "}
      <p className="text-gray-font text-sm font-normal mb-4">
        Registration fees are playable in U.S. dollars. If you have any
        questions regarding payment, please contact WSG at{" "}
        <strong className="text-Default underline">
          events@worldservicesgroup.com
        </strong>{" "}
        or via phone at +1 713 650 0333.
      </p>
      <div className="w-full bg-gray-15">
        <div className="flex flex-col items-start h-auto pt-2 md:pt-4 pb-3 md:pb-6 px-0 bg-gray-15 transition-all text-left w-full overflow-hidden">
          <div className="border-b border-primary pb-0 flex-shrink-0 mb-4">
            <h3 className="text-sm md:text-base lg:text-lg font-normal text-gray-font leading-4 md:leading-5 mb-2 text-left font-sans pl-4 md:pl-8">
              Payment Notification
            </h3>
          </div>
          <div className="grid grid-cols-12 gap-6 text-left px-4 md:px-8 flex-1 w-full overflow-hidden">
            <div className="col-span-12 md:col-span-6 lg:col-span-4 flex flex-col gap-2 w-full text-gray-font">
              <label className="text-base md:text-lg font-semibold">Name</label>
              <span>
                {[loggedPerson?.name, loggedPerson?.lastName]
                  .filter(Boolean)
                  .join(" ")}
              </span>
            </div>

            <div className="col-span-12 md:col-span-6 lg:col-span-4 flex flex-col gap-2 w-full text-gray-font">
              <label className="text-base md:text-lg font-semibold">
                Email
              </label>
              <span>{loggedPerson?.firmEmail ?? ""}</span>
            </div>

            <div className="col-span-12 lg:col-span-4 flex flex-col gap-2 w-full text-gray-font">
              <label className="text-base md:text-lg font-semibold">
                Invoice Address
              </label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(invoiceAddressTypeEnum).map(
                  ([key, value], index) => (
                    <RadioButton
                      key={key}
                      index={index}
                      text={value as string}
                      selected={selectedInvoiceAddress === key}
                      handleRadioButtonChange={() =>
                        setSelectedInvoiceAddress(key as InvoiceAddressType)
                      }
                      name="invoiceAddress"
                    />
                  )
                )}
              </div>
            </div>

            <div className="col-span-12 lg:col-span-6 text-gray-font">
              <Input
                title="CC. Name"
                name="ccName"
                value={cc.name}
                onChange={(e) => setCc({ ...cc, name: e.target.value })}
                placeholder=""
                error={false}
                required={false}
                className="!bg-white"
              />
            </div>
            <div className="col-span-12 lg:col-span-6 text-gray-font">
              <Input
                title="CC. Email"
                name="ccEmail"
                value={cc.email}
                onChange={(e) => {
                  const newEmail = e.target.value;
                  setCc({ ...cc, email: newEmail });

                  if (eventId) {
                    if (newEmail.trim()) {
                      const emailValidation =
                        validateAndSanitizeEmail(newEmail);
                      if (
                        emailValidation.isValid &&
                        emailValidation.sanitized
                      ) {
                        sessionStorage.setItem(
                          `ccEmail_${eventId}`,
                          emailValidation.sanitized
                        );
                      } else {
                        sessionStorage.removeItem(`ccEmail_${eventId}`);
                      }
                    } else {
                      sessionStorage.removeItem(`ccEmail_${eventId}`);
                    }
                  }
                }}
                placeholder=""
                error={false}
                required={false}
                className="!bg-white"
              />
            </div>
          </div>
        </div>
      </div>
      <iframe
        key={`${payaUrl}-${iframeKey}`}
        src={payaUrl}
        title="Paya Payment Form"
        className="w-full h-[1500px] md:h-[1500px] lg:h-[1800px] min-h-[1500px] md:min-h-[1500px] lg:min-h-[1800px]"
        allow="payment"
        onLoad={handleIframeLoad}
        onError={() => {
          const errorMsg = "Error loading payment form. Please try again.";
          toastError(errorMsg);
          setError(errorMsg);
        }}
        style={{
          border: "none",
          display: "block",
        }}
      />
    </div>
  );
};

export default EventPaymentPageWithLayout;
